import { Extension } from '@tiptap/core';
import type MarkdownIt from 'markdown-it';
import type StateCore from 'markdown-it/lib/rules_core/state_core';
import type Token from 'markdown-it/lib/token';

const unwrapBacktickedLinkInner = (inner: string): string | null => {
    if (!inner.startsWith('[')) return null;

    const closeBracketIndex = inner.indexOf(']');
    if (closeBracketIndex < 0) return null;
    if (inner[closeBracketIndex + 1] !== '(') return null;

    let depth = 0;
    for (let i = closeBracketIndex + 2; i < inner.length; i++) {
        const ch = inner[i];
        if (ch === '(') depth++;
        if (ch === ')') {
            if (depth === 0) {
                return i === inner.length - 1 ? inner : null;
            }
            depth--;
        }
    }

    return null;
};

const unwrapBacktickedLink = (input: string): string => {
    let out = '';
    let i = 0;

    while (i < input.length) {
        const start = input.indexOf('`[', i);
        if (start < 0) {
            out += input.slice(i);
            break;
        }

        out += input.slice(i, start);
        const end = input.indexOf('`', start + 1);
        if (end < 0) {
            out += input.slice(start);
            break;
        }

        const inner = input.slice(start + 1, end);
        const unwrapped = unwrapBacktickedLinkInner(inner);
        out += unwrapped ?? input.slice(start, end + 1);
        i = end + 1;
    }

    return out;
};

const unwrapBacktickedRef = (input: string): string => {
    return input.replace(/`(\[#REF:\s*[^\]]+\])`/g, '$1');
};

const unwrapBacktickedPatterns = (content: string): string => {
    let next = content;
    next = unwrapBacktickedLink(next);
    next = unwrapBacktickedRef(next);
    return next;
};

export const BacktickUnwrapper = Extension.create({
    name: 'backtickUnwrapper',

    addStorage() {
        return {
            markdown: {
                parse: {
                    setup(md: MarkdownIt) {
                        md.core.ruler.before('inline', 'backtick_unwrap', (state: StateCore) => {
                            state.tokens.forEach((token: Token) => {
                                if (token.type === 'inline' && token.content) {
                                    token.content = unwrapBacktickedPatterns(token.content);
                                }
                            });
                        });
                    },
                },
            },
        };
    },
});
